// antd v5 theme tokens. Mirrors styles/global.scss CSS custom properties so the
// React component layer (antd Buttons, Tabs, Modal, Select, etc.) shares the
// same palette and metrics as the SCSS layer. Imported by App.js.
//
// antd resolves its tokens once at ConfigProvider level and inlines the result
// into generated class names, so it cannot read `var(--*)` and cannot react to
// the `data-theme` attribute on its own. The palette therefore has to be split
// per mode here and re-supplied whenever the theme flips — see buildAntdTheme.

import { theme } from 'antd';

// Shared across both modes: shape, motion and typography never flip.
const sharedToken = {
  colorInfo:    '#06B6D4',
  colorSuccess: '#10B981',
  colorWarning: '#F59E0B',
  colorError:   '#EF4444',

  fontFamily:
    "'Pretendard', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Arial, 'Noto Sans KR', sans-serif",
  fontSize: 14,
  fontSizeLG: 16,
  fontSizeSM: 13,

  borderRadius:   8,
  borderRadiusLG: 12,
  borderRadiusSM: 6,

  motionDurationFast: '0.15s',
  motionDurationMid:  '0.2s',
  motionDurationSlow: '0.3s',

  // Wireframe off, prefer filled controls
  wireframe: false,
};

const sharedComponents = {
  Button: {
    controlHeight: 36,
    controlHeightLG: 44,
    controlHeightSM: 28,
    fontWeight: 500,
  },
  Modal: {
    borderRadiusLG: 16,
  },
  Select: {
    controlHeight: 36,
  },
  Input: {
    controlHeight: 36,
  },
};

// Light — unchanged from the original hard-coded theme.
const light = {
  token: {
    colorPrimary:   '#0EA5E9',
    colorLink:      '#0EA5E9',
    colorLinkHover: '#0284C7',

    colorText:           '#0F172A',
    colorTextSecondary:  '#475569',
    colorTextTertiary:   '#64748B',
    colorTextQuaternary: '#94A3B8',
    colorBorder:         '#E2E8F0',
    colorBorderSecondary:'#F1F5F9',
    colorBgContainer:    '#FFFFFF',
    colorBgElevated:     '#FFFFFF',
    colorBgLayout:       '#F8FAFC',
    colorBgSpotlight:    '#0F172A',
  },
  components: {
    Tabs: {
      itemActiveColor: '#0EA5E9',
      itemHoverColor: '#0284C7',
      itemSelectedColor: '#0EA5E9',
      inkBarColor: '#0EA5E9',
    },
    Table: {
      headerBg: '#F8FAFC',
      headerColor: '#475569',
      rowHoverBg: '#F1F5F9',
    },
  },
};

// Dark — slate palette, matching the [data-theme="dark"] block in global.scss.
// colorBgBase seeds the algorithm's derived neutrals; the explicit tokens below
// then pin the surfaces we care about so they line up with the SCSS layer
// exactly rather than drifting to antd's own generated greys.
const dark = {
  token: {
    colorPrimary:   '#38BDF8',   // sky-400
    colorLink:      '#38BDF8',
    colorLinkHover: '#7DD3FC',

    colorBgBase:   '#0F172A',
    colorTextBase: '#F1F5F9',

    colorText:           '#F1F5F9',   // slate-100
    colorTextSecondary:  '#CBD5E1',   // slate-300
    colorTextTertiary:   '#94A3B8',   // slate-400
    colorTextQuaternary: '#64748B',   // slate-500

    colorBorder:          '#3A4A61',  // --color-field-border
    colorBorderSecondary: '#334155',  // slate-700

    // Controls and popups sit one step above the page base so a Select or
    // Input reads as a lifted panel rather than a hole in the layout.
    colorBgContainer: '#1E293B',   // slate-800 — inputs, selects, table body
    colorBgElevated:  '#233145',   // dropdowns, modals, popovers
    colorBgLayout:    '#0F172A',   // slate-900 — page base
    colorBgSpotlight: '#334155',   // tooltips
  },
  components: {
    Tabs: {
      itemActiveColor: '#38BDF8',
      itemHoverColor: '#7DD3FC',
      itemSelectedColor: '#38BDF8',
      inkBarColor: '#38BDF8',
    },
    Table: {
      headerBg: '#273449',
      headerColor: '#CBD5E1',
      rowHoverBg: '#2E3E56',
    },
  },
};

// `mode` comes from ThemeContext ('light' | 'dark').
//
// Both merges are shallow. Today the two halves are disjoint — shared owns
// Button/Modal/Select/Input, the presets own Tabs/Table — but adding a component
// key to a preset that shared also declares would drop the shared settings
// wholesale rather than merging them. Keep the two lists disjoint.
export function buildAntdTheme(mode) {
  const preset = mode === 'dark' ? dark : light;
  return {
    algorithm: mode === 'dark' ? theme.darkAlgorithm : theme.defaultAlgorithm,
    token: { ...sharedToken, ...preset.token },
    components: { ...sharedComponents, ...preset.components },
  };
}

export default buildAntdTheme;
